import { Button } from "@/components/ui/baseUi/button"
import { UserCell } from "@/components/ui/layout/table/userCell"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import ChatEmpty from "@images/empty/chat_empty.svg"
import { BoxLeadEntity } from "@modules/boxLead/entities/boxLeadEntity"
import { LeadColumnToSort } from "@modules/boxLead/gateway/boxLeadDto"
import { usePersistedBoxLeads } from "@modules/boxLead/hooks/useBoxLead"
import { useBoxLeadDetailsSidebar } from "@modules/boxLead/hooks/useBoxLeadDetailsSidebar"
import { useBoxLeadSidebar } from "@modules/boxLead/hooks/useBoxLeadSidebar"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { ColumnSortingOrder } from "@modules/types/datatableTypes"
import { HUDatatable } from "@modules/ui/components/dataTables/huDatatable"
import { HeaderTemplate } from "@modules/ui/components/dataTables/templates/headerTemplate"
import { EmptyView } from "@modules/ui/components/emptyView"
import { HuNote } from "@modules/ui/components/huNote"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import { createColumnHelper } from "@tanstack/react-table"
import dayjs from "dayjs"
import { Plus } from "lucide-react"
import { useEffect, useState } from "react"

type BoxLeadTableProps = {
  setCountTab: (count: number) => void
  className?: string
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<BoxLeadEntity>()

export function BoxLeadTable({ setCountTab, className, ...props }: BoxLeadTableProps) {
  const [selectedLead, setSelectedLead] = useState<BoxLeadEntity>()
  const { navigateToLeadDetailsInfo } = useBoxLeadDetailsSidebar()
  const { navigateToBoxLeadForm } = useBoxLeadSidebar()

  const canAddLead = useBoxStaffPermission(BoxStaffPermissions.MANAGE_LEAD)

  const {
    data,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage,
    hasNextPage,
    totalElements,
    setSorting,
    sortBy,
    sortOrder,
    isSearching,
  } = usePersistedBoxLeads()

  useEffect(() => {
    if (totalElements) {
      setCountTab(totalElements)
    } else setCountTab(0)
  }, [data])

  const columns = [
    columnHelper.accessor("user", {
      header: () => (
        <HeaderTemplate
          title={word("global.table.headerColumn.name")}
          variant={
            sortBy === LeadColumnToSort.NAME
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(LeadColumnToSort.NAME, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(LeadColumnToSort.NAME, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: lead } }) => (
        <UserCell
          member={{
            avatar: lead.user?.avatar,
            firstname: lead.user?.firstname ?? lead.firstname ?? "",
            lastname: lead.user?.lastname ?? lead.lastname ?? "",
          }}
          subtitle={lead.getUserName()}
          subtitleColor="neutral"
        />
      ),
    }),
    columnHelper.accessor("leadType", {
      header: () => (
        <HeaderTemplate
          title={word("global.table.headerColumn.type")}
          variant={
            sortBy === LeadColumnToSort.LEAD_TYPE
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(LeadColumnToSort.LEAD_TYPE, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(LeadColumnToSort.LEAD_TYPE, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: lead } }) => (
        <Text fontStyle="TS" className="text-textDefault-body">
          {lead.getWordingType()}
        </Text>
      ),
    }),
    columnHelper.accessor("firstContact", {
      header: () => <HeaderTemplate title={word("global.table.headerColumn.firstContact")} />,
      cell: ({ row: { original: lead } }) => (
        <Text fontStyle="BM" className="text-textDefault-body">
          {formatDateWithHyphen(dayjs(lead.firstContact).format("L"))}
        </Text>
      ),
    }),
    columnHelper.accessor("note", {
      header: () => <HeaderTemplate title={word("global.table.headerColumn.note")} />,
      cell: ({ row: { original: lead } }) => <HuNote id={lead.id} index={lead.id} note={lead.note ?? ""} />,
    }),
  ]

  const onSelectedLead = (e: BoxLeadEntity) => {
    navigateToLeadDetailsInfo(e.id, {
      onHide: () => {
        setSelectedLead(undefined)
        return Promise.resolve(true)
      },
      replaceAllComponents: true,
    })
    setSelectedLead(e)
  }

  if (data.length === 0) {
    return (
      <EmptyView
        image={{ src: ChatEmpty }}
        title={isSearching ? word("searching.list.empty.title") : word("lead.list.empty.title")}
        description={isSearching ? word("searching.list.empty.description") : word("lead.list.empty.description")}
        paddingType="In_table"
      >
        {canAddLead && !isSearching && (
          <Button variant="primary" size="lg" className="mx-auto" onClick={() => navigateToBoxLeadForm()}>
            <Plus size={16} color="currentColor" />
            {word("member.button.addLead")}
          </Button>
        )}
      </EmptyView>
    )
  }

  return (
    <div className={cn("flex flex-col px-6", className)} {...props}>
      <HUDatatable
        data={data}
        columns={columns}
        pageSize={pageSize}
        setPageSize={setPageSize}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        handleRowSelectionChange={(row: BoxLeadEntity) => onSelectedLead(row)}
        selectedRowIndex={data.findIndex((item) => item.id === selectedLead?.id)}
        totalElements={totalElements}
      />
    </div>
  )
}
